import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { RolaKonta } from "@shared/kontaTypy.js";

/**
 * Konto zalogowanego uzytkownika dla calego interfejsu: nazwa i rola.
 *
 * Rola steruje WYLACZNIE tym, co ekran pokazuje (menu, trasy, techniczne karty). Granica bezpieczenstwa jest na serwerze:
 * `user` dostaje 403 na trasach admina i widzi tylko wlasne analizy, nawet gdyby ktos obszedl interfejs.
 *
 * Domyslna wartosc kontekstu to admin. To swiadome: bez dostawcy (testy pojedynczych widokow, poligon /_ds) widok ma
 * wygladac jak dotychczas, a konto "user" ustawia bramka w App.tsx. Tryb uproszczony wlacza sie wiec jawnie, nigdy przez
 * przypadek braku kontekstu.
 */

export interface Konto {
  username: string;
  role: RolaKonta;
  /** Skrot dla warunkow w widokach: true tylko dla roli `admin`. */
  czyAdmin: boolean;
}

export function kontoZRoli(role: RolaKonta, username = ""): Konto {
  return { username, role, czyAdmin: role === "admin" };
}

const KontoContext = createContext<Konto>(kontoZRoli("admin"));

export function KontoProvider({
  username,
  role,
  children,
}: {
  username: string;
  role: RolaKonta;
  children: ReactNode;
}): React.JSX.Element {
  const konto = useMemo(() => kontoZRoli(role, username), [role, username]);
  return <KontoContext.Provider value={konto}>{children}</KontoContext.Provider>;
}

export function useKonto(): Konto {
  return useContext(KontoContext);
}
